import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { isEmpty } from 'ramda';
import TextField from '@material-ui/core/TextField';

import { Iso6391Locale, LocalizedPlainStringValue } from 'yeself-common/typings/model/Model';

type LocalizedPlainStringFieldProps = Omit<
    React.ComponentProps<typeof TextField>,
    'type' | 'value' | 'onChange'
> & {
    locale: {
        code: Iso6391Locale;
        label?: string;
    };
    fallbackLocale: Iso6391Locale;
    value?: LocalizedPlainStringValue;
    meta?: {
        error?: string;
        invalid: boolean;
        touched: boolean;
        valid: boolean;
        warning?: string;
    };
    onChange?: (value: LocalizedPlainStringValue) => void;
    onBlur?: (value: LocalizedPlainStringValue) => void;
};

const LocalizedPlainStringField = ({
    value = {},
    onChange,
    onBlur,
    locale,
    fallbackLocale,
    meta,
    ...props
}: LocalizedPlainStringFieldProps): React.ReactElement => {
    const intl = useIntl();
    const { code, label } = locale;

    const handleOnChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (onChange)
                onChange({
                    ...value,
                    [code]: event.target.value,
                });
        },
        [value, onChange, code]
    );

    const handleOnBlur = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (onBlur)
                onBlur({
                    ...value,
                    [code]: event.target.value,
                });
        },
        [value, onBlur, code]
    );

    const resolvedMissingTranslate = label
        ? `${label} ${intl.formatMessage({ id: 'missingToTranslate' })}: ${
              value[fallbackLocale] || Object.values(value).filter(Boolean)[0]
          }`
        : `${code} ${intl.formatMessage({ id: 'missingToTranslate' })}: ${
              value[fallbackLocale] || Object.values(value).filter(Boolean)[0]
          }`;

    const newTranslate = isEmpty(value) ? '' : resolvedMissingTranslate;

    return (
        <TextField
            fullWidth
            value={value[code] ?? newTranslate}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            {...props}
            type="text"
            error={meta && meta.touched && meta.invalid}
            helperText={meta && meta.touched && meta.error}
        />
    );
};

export default LocalizedPlainStringField;
